import { $, CustomEventWithDetail, html, View } from 'rune-ts';
import klass from './SearchModuleCreatorAll.module.scss';
import {
  AutoCompleteClickEvent,
  AutoCompleteList,
  AutoCompleteListOption,
} from '@services/MarppleShop/renderApp/components/cells/SearchModule/view/AutoCompleteList.ts';
import {
  InputSearch,
  SearchArrowKeydownEvent,
  SearchDebounceInputEvent,
  SearchInputEvent,
  SearchSubmitEvent,
} from '@services/MarppleShop/renderApp/components/atoms/InputSearch/InputSearch.ts';
import { RecentSearchesSection } from '@services/MarppleShop/renderApp/components/cells/SearchModule/view/RecentSearchesSection.ts';
import { AutoCompleteController } from '@services/MarppleShop/renderApp/components/cells/SearchModule/controllers/AutoCompleteController.ts';
import { SearchModuleController } from '@services/MarppleShop/renderApp/components/cells/SearchModule/controllers/SearchModuleController.ts';
import axios from 'axios';
import { SearchApiS } from '@services/MarppleShop/features/Search/outbound/share.ts';
import { AutoComplete } from '@services/MarppleShop/features/Search/type/autocomplete.ts';
import { RecentSearchesSectionController } from '@services/MarppleShop/renderApp/components/cells/SearchModule/controllers/RecentSearchesSectionController.ts';
import { SearchData } from '@services/MarppleShop/renderApp/components/cells/SearchModule/SearchModule.ts';
import { unEscaper } from '@modules/Util/S/Function/util';
import { htmlIf } from '@services/MarppleShop/shared/util';
import util_klass from '@services/MarppleShop/renderApp/components/cells/SearchModule/view/SearchModuleUtil.module.scss';
import { CloseIcon } from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { makeExploreScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { postSearchLogApi } from '@services/MarppleShop/features/Search/api';

export interface CreatorSearchModuleData {
  value?: string;
  placeholder?: string;
}

type SearchModuleCreatorShopOption = Pick<AutoCompleteListOption, 'highlight'> & {
  klass?: string;
  without_visible_interaction?: boolean;
  is_mobile: boolean;
  search_input_left_part?: View;
};

export class SearchModuleCreatorAllCloseEvent extends CustomEventWithDetail<string> {}

export class SearchModuleCreatorAll extends View<CreatorSearchModuleData> {
  public InputSearchView: InputSearch;
  public AutoCompleteListView: AutoCompleteList;
  public AutoCompleteController: AutoCompleteController;
  public RecentSearchesSectionView: RecentSearchesSection;
  public SearchModuleController: SearchModuleController;

  public static CloseEvent = SearchModuleCreatorAllCloseEvent;

  constructor(
    data: CreatorSearchModuleData,
    private option: SearchModuleCreatorShopOption,
  ) {
    super(data, option);

    this.InputSearchView = new InputSearch({
      value: data.value,
      placeholder: data.placeholder,
      klass: klass.search_input,
      debounce_ms: 200,
    });

    this.AutoCompleteListView = new AutoCompleteList({
      klass: {
        list: klass.auto_complete_list,
        item: klass.auto_complete_item,
        focused: klass.focused,
      },
      option: {
        highlight: option.highlight,
      },
    });

    this.AutoCompleteController = new AutoCompleteController(
      {
        search: data.value,
      },
      {
        onRequestAutoComplete: this.onRequestAutoComplete.bind(this),
      },
      {
        minCharsForAutoComplete: 1,
        customCondition(_, alreadySubmitted) {
          return !alreadySubmitted;
        },
      },
    );

    this.RecentSearchesSectionView = new RecentSearchesSection(
      {
        recent_searches: [],
        is_mobile: option.is_mobile,
      },
      {
        klass: {
          searches_container: klass.searches_container,
          container: klass.recent_search_section,
          hide: klass.hide,
        },
        recent_search_options: {
          theme: 'light',
          button_close_props: {},
          makeSearchUrl: this.makeSearchUrl,
          makeSearchNavigate: makeExploreScreenNavigate,
          klass: klass.recent_search,
        },
      },
      new RecentSearchesSectionController({
        onRemoveAll: this.onRemoveAllApi,
        onRemoveOne: this.onRemoveOneApi,
      }),
    );

    this.SearchModuleController = new SearchModuleController(this);
  }

  override template() {
    const _klass = this.option.without_visible_interaction
      ? util_klass.visible
      : `${util_klass.hidden} ${util_klass.hiding}`;
    const is_mobile = this.option.is_mobile;

    return html`
      <div class="${klass.search_module} ${_klass} ${this.option.klass}">
        <div class="${klass.input_section}">
          <div class="${klass.search_input_container}">${this.InputSearchView}</div>
          ${htmlIf(html`<span class="${klass.close_icon}">${CloseIcon()}</span>`, is_mobile)}
        </div>
        <div class="${klass.search_section}">
          <!-- 자동 완성 -->
          ${this.AutoCompleteListView}
          <!-- 최근 검색어 -->
          ${this.RecentSearchesSectionView}
        </div>
      </div>
    `;
  }

  override async onRender() {
    const [{ data: recents }, auto_completes] = await Promise.all([
      axios.get<{ queries: string[] }>(`/${ET.lang}/@api/keywords/creator/history`),
      this.onRequestAutoComplete(this.data.value),
    ]);

    this.AutoCompleteListView.setData({ auto_completes, value: this.data.value ?? '' });
    this.RecentSearchesSectionView.setData({
      recent_searches: recents.queries.map((search) => ({ name: search })),
    });
    this.redraw();
    this.changeSearchType(auto_completes.length ? 'auto-complete' : 'recent');
  }

  override onMount() {
    this.delegate(SearchInputEvent, InputSearch, (e) => {
      this.setValue(e.detail);

      // 자동완성이 가능한 경우는 `SearchDebounceInputEvent` 에서 처리하므로 즉시 종료
      if (this.AutoCompleteController.canAutoComplete()) {
        return;
      }
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();
      // 자동완성 안 되는 경우는 자동완성 목록 가림
      this.changeSearchType('recent');
    });

    this.delegate(SearchDebounceInputEvent, InputSearch, async () => {
      const auto_completes = await this.AutoCompleteController.handleAutoComplete();
      if (this.option.is_mobile) {
        this.handleChangeMo(auto_completes || [], this.AutoCompleteController.getSearch());
      } else {
        this.handleChangePc(auto_completes || [], this.AutoCompleteController.getSearch());
      }
    });

    this.delegate(AutoCompleteClickEvent, AutoCompleteList, (e) => {
      const search = e.detail;
      if (!search) {
        return;
      }
      postSearchLogApi({ search, is_popular_search_word: false });
      return this.confirmSearch(search);
    });

    // submit
    this.delegate(SearchSubmitEvent, InputSearch, async (e) => {
      if (!e.detail) {
        location.href = this.makeSearchUrl(e.detail);
        return;
      }

      // 검색어 자동완성 즉시 숨김
      this.changeSearchType('recent');
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();

      let search: string = e.detail;
      const focused_el = this.AutoCompleteListView.getFocusedEl();
      if (focused_el?.dataset?.search) {
        search = focused_el.dataset.search;
      }

      this.setValue(e.detail);
      this.InputSearchView.setValue(search);
      postSearchLogApi({ search, is_popular_search_word: false });
      await this.confirmSearch(search);
    });

    // key up/down event
    this.delegate(SearchArrowKeydownEvent, InputSearch, (e) => {
      const key = e.detail;
      if (key === 'ArrowUp') {
        this.AutoCompleteListView.moveFocusUp();
      } else if (key === 'ArrowDown') {
        this.AutoCompleteListView.moveFocusDown();
      }
    });

    this.SearchModuleController.onMount();

    // mobile only
    if (this.option.is_mobile) {
      this.delegate('click', `.${klass.close_icon}`, () => {
        this.dispatchEvent(SearchModuleCreatorAll.CloseEvent, {
          bubbles: true,
          detail: this.InputSearchView.getValue(),
        });
      });
    }
  }

  open() {
    this.SearchModuleController.open();
    this.InputSearchView.focus();
  }

  close(): string {
    this.SearchModuleController.close();
    return this.InputSearchView.getValue();
  }

  // 검색어 최근 검색어에 추가하고, 결과로 이동
  private async confirmSearch(search: string) {
    const url = this.makeSearchUrl(search);
    this.InputSearchView.setValue(search);
    await axios.post(`/${ET.lang}/@api/keywords/creator/history`, null, {
      params: { q: search },
    });
    location.href = url;
  }

  async onRequestAutoComplete(value?: string) {
    try {
      const res = await SearchApiS.getCreatorAutoCompleteListApi({
        search: value,
      });
      return res.auto_completes;
    } catch (e) {
      return [];
    }
  }

  private setValue(value?: string) {
    this.data.value = value;
    this.AutoCompleteController.setSearch(value);
  }

  private handleChangePc(auto_completes: AutoComplete[], value?: string) {
    if (value) {
      if (auto_completes.length > 0) {
        this.AutoCompleteListView.setData({ auto_completes, value: value ?? '' });
        this.AutoCompleteListView.redraw();
        this.changeSearchType('auto-complete');
      } else {
        if (this.AutoCompleteListView.data.auto_completes.length > 0) {
          this.changeSearchType('auto-complete');
        } else {
          this.changeSearchType('hide');
        }
      }
    } else {
      this.changeSearchType('recent');
    }
  }

  private handleChangeMo(auto_completes: AutoComplete[], value?: string) {
    this.AutoCompleteListView.setData({ auto_completes, value: value ?? '' });
    this.AutoCompleteListView.redraw();
    this.changeSearchType(auto_completes.length ? 'auto-complete' : 'recent');
  }

  private changeSearchType(type: 'auto-complete' | 'recent' | 'hide') {
    if (type === 'recent') {
      this.AutoCompleteListView.hide();
      this.RecentSearchesSectionView.show();
    } else if (type === 'auto-complete') {
      this.AutoCompleteListView.show();
      this.RecentSearchesSectionView.hide();
    } else {
      this.AutoCompleteListView.hide();
      this.RecentSearchesSectionView.hide();
    }

    $(this.element()).find(`.${klass.search_section}`)?.setAttribute('data-type', type);
  }

  private onRemoveAllApi = async () => {
    await axios.delete(`/${T.lang}/@api/keywords/creator/history/all`);
  };

  private onRemoveOneApi = async (search: string) => {
    const { data } = await axios.delete<{ queries: string[] }>(`/${T.lang}/@api/keywords/creator/history`, {
      params: { q: search },
    });
    return data.queries.map((name) => ({ name })) as SearchData[];
  };

  private makeSearchUrl = (search: string): string => {
    const path = `/${ET.lang}/@/creator/explore`;

    if (!search) {
      return path;
    }

    return path + '?search=' + encodeURIComponent(unEscaper(search));
  };
}
